import React from 'react';
import BaseStep from './BaseStep';
import Loader from '../api/Loader';
import SequenceLoader from '../api/SequenceLoader';

/**
 * @typedef {import('../types/CampaignSchedule').CampaignScheduleEntry} CampaignScheduleEntry
 * @typedef {import('../data/WizardData').default} WizardData
 */

export default class SequenceConfigStep extends BaseStep {
  static id = 'sequence-config-step';

  static title = 'Sequence Configuration';

  /**
   * The key representing a custom resolution.
   * @type {string}
   */
  static CUSTOM_RES_KEY = 'custom';

  /**
   * Available resolutions for hte dropdown
   * @type {Object.<string, {label: string, x: number|null, y: number|null}>}
   */
  static RESOLUTIONS = {
    r1920x1080: { label: 'Landscape (1920x1080)', x: 1920, y: 1080 },
    r1080x1920: { label: 'Portrait (1080x1920)', x: 1080, y: 1920 },
    r3840x2160: { label: '4K Landscape (3840x2160)', x: 3840, y: 2160 },
    r2160x3840: { label: '4K Portrait (2160x3840)', x: 2160, y: 3840 },
    [SequenceConfigStep.CUSTOM_RES_KEY]: { label: 'Custom resolution', x: null, y: null },
  };

  /**
   * Triggers data load for campaign schedules.
   * @see super.wizardData.api_campaignSchedules
   * @see super.wizardData.api_allSequences
   * @see super.wizardData.sequence
   */
  static triggerDataLoad() {
    if (SequenceConfigStep.isCampaignScheduleWaitingForLoad()) {
      Loader.loadCampaignSchedules()
        .then(response => {
          const schedules = response?.campaignScheduleEntries || [];

          if (schedules.length === 0) {
            super.notification.error(
              'No campaign schedules found. You must have at least one campaign in your schedule.',
            );
          }

          SequenceConfigStep.initSchedulesAndTargetCampaign(schedules);
        })
        .catch(error => {
          super.notification.error('Network error while loading campaign schedules', error);
        });
    }

    if (SequenceConfigStep.isAllSequencesWaitingForLoad()) {
      Loader.loadAllSequences()
        .then(result => {
          super.wizardData.api_allSequences = result;
        })
        .catch(error => {
          super.notification.error('Network error while loading sequences', error);
        });
    }

    if (SequenceConfigStep.isEditSequenceWaitingForLoad()) {
      SequenceLoader.loadSequence(super.wizardData.editSequenceId).then(sequence => {
        if (sequence) {
          super.notification.info(`Loaded sequence for work. You are editing "${sequence.name}".`);
        }

        super.wizardData.sequence = sequence;
      });
    }
  }

  /**
   * Determines if campaign schedules data is not yet loaded.
   * empty campaign schedules will be treated as not loaded
   * might cause infinite loading if there are actually no campaign schedules,
   * this is an edge case, an error message will be shown in this case and user
   * will not be able to proceed
   * @returns {boolean} True if waiting for load, false otherwise.
   */
  static isCampaignScheduleWaitingForLoad() {
    return !super.wizardData.api_campaignSchedules.length;
  }

  /**
   * Initializes campaign schedules and sets the target campaign.
   * @param {CampaignScheduleEntry[]} schedules - The array of campaign schedule entries to initialize with.
   * @see super.wizardData.api_campaignSchedules
   * @see super.wizardData.targetCampaign
   */
  static initSchedulesAndTargetCampaign(schedules) {
    super.wizardData.api_campaignSchedules = schedules;
    super.wizardData.targetCampaign = schedules.find(c => c.isActive);
  }

  /**
   * Determines if all sequences are loaded.
   * @returns {boolean} True if waiting for load, false otherwise.
   */
  static isAllSequencesWaitingForLoad() {
    return !super.wizardData.api_allSequences;
  }

  /**
   * Determines if the sequence being edited is still loading.
   * @returns {boolean} True if waiting for load, false otherwise.
   */
  static isEditSequenceWaitingForLoad() {
    return (super.wizardData.sequence?.id || null) !== super.wizardData.editSequenceId;
  }

  /**
   * Determines if the necessary data is still loading.
   * @returns {boolean} True if waiting for load, false otherwise.
   */
  static dataIsLoading() {
    return (
      SequenceConfigStep.isCampaignScheduleWaitingForLoad() ||
      SequenceConfigStep.isAllSequencesWaitingForLoad() ||
      SequenceConfigStep.isEditSequenceWaitingForLoad()
    );
  }

  /**
   * Gets the data needed for rendering.
   * @returns {boolean} True if can proceed, false otherwise.
   */
  static canProceed() {
    return (
      super.wizardData.isTargetCampaignValid().isValid &&
      super.wizardData.isNameValid().isValid &&
      super.wizardData.isResolutionValid().isValid
    );
  }

  // Object methods BELOW -----------------------------------------------------

  /**
   * @param {Object} props - The component props.
   */
  constructor(props) {
    super(props);

    this.state = {
      nameInput: this.wizardData.name,
      resolutionX: this.wizardData.resolution.x,
      resolutionY: this.wizardData.resolution.y,
    };
  }

  /**
   * Finds a campaign schedule by campaign ID.
   * @param {number} campaignId - The ID of the campaign to find.
   * @returns {CampaignScheduleEntry|undefined} The found campaign schedule entry, or undefined if not found.
   */
  findCampaignScheduleByCampaignId(campaignId) {
    return this.wizardData.api_campaignSchedules.find(c => c.campaignId === campaignId);
  }

  /**
   * Gets the resolution key based on current resolution.
   * @returns {string} The resolution key.
   */
  getResolutionKey() {
    const { x, y } = this.wizardData.resolution;
    const key = `r${x}x${y}`;

    return SequenceConfigStep.RESOLUTIONS[key] ? key : SequenceConfigStep.CUSTOM_RES_KEY;
  }

  /**
   * Updates the name in the data object
   * @param {Event} e - The change event.
   * @see this.wizardData.name
   */
  onChangeName = e => {
    const newInput = e.target.value;

    this.setState({ nameInput: newInput });

    // if the last char of this.state.nameInput is not space, update wizardData.name
    // this prevents name normalization from trimming spaces while user is typing
    if (newInput.endsWith(' ') === false) {
      this.wizardData.name = e.target.value;
    }
  };

  /**
   * Handles the key up event on the name input.
   * @param {Event} e - The key up event.
   */
  onKeyUpName = e => {
    if (e.keyCode === 13 && SequenceConfigStep.canProceed()) {
      this.wizardData.navigationNextStep();
    }
  };

  /**
   * Handles the target campaign dropdown change event.
   * @param {Event} e - The change event.
   * @see this.wizardData.targetCampaign
   */
  onTargetCampaignDropdown = e => {
    const selectedCampaign = this.findCampaignScheduleByCampaignId(
      this.stringToInt(e.target.value),
    );

    this.wizardData.targetCampaign = selectedCampaign || null;
  };

  /**
   * Handles the resolution dropdown change event.
   * @param {Event} e - The change event.
   * @see this.wizardData.resolution
   */
  onChangeResolutionDropdown = e => {
    let { x, y } = SequenceConfigStep.RESOLUTIONS[e.target.value];

    x = x === null ? this.wizardData.resolution.x : x;
    y = y === null ? this.wizardData.resolution.y : y;

    this.setState({ resolutionX: x, resolutionY: y });
    this.wizardData.resolution = { x, y };
  };

  /**
   * Handles the resolution X input change event.
   * @param {Event} e - The change event.
   * @see this.wizardData.resolution
   */
  onChangeResolutionX = e => {
    const { y } = this.wizardData.resolution;
    const newValue = e.target.value;

    this.setState({ resolutionX: newValue });
    this.wizardData.resolution = { x: this.stringToInt(e.target.value), y };
  };

  /**
   * Handles the resolution Y input change event.
   * @param {Event} e - The change event.
   * @see this.wizardData.resolution
   */
  onChangeResolutionY = e => {
    const { x } = this.wizardData.resolution;
    const newValue = e.target.value;

    this.setState({ resolutionY: newValue });
    this.wizardData.resolution = { x, y: this.stringToInt(e.target.value) };
  };

  /**
   * Renders the step component.
   * @returns {JSX.Element} The step component.
   */
  render() {
    const nameValidation = this.wizardData.isNameValid();
    const resolutionValidation = this.wizardData.isResolutionValid();
    const resolutionXValidation = this.wizardData.isResolutionXValid();
    const resolutionYValidation = this.wizardData.isResolutionYValid();
    const campaignValidation = this.wizardData.isTargetCampaignValid();
    const resolutionKey = this.getResolutionKey();

    return (
      <div className="sequenceconfig-step">
        <div className="welcome-banner-container">
          <div className="banner-points">
            <h2>Welcome to Signage Wizard</h2>
            <h4>This is a quick way to put content on screens in 4 simple steps:</h4>
            <ol>
              <li>Name your sequence and choose the resolution that matches your screen. </li>
              <li>Add your content.</li>
              <li>Choose the screens you want to display the content on.</li>
              <li>Assign and Deploy.</li>
            </ol>
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="configstep-input">
            Sequence Name <span className="required">*</span>
          </label>
          {this.state.nameInput && !nameValidation.isValid && (
            <span className="text-warning" data-error-code={nameValidation.code}>
              <span className="material-icons icon-warning" title={`${nameValidation.message}`}>
                error_outline
              </span>
              {nameValidation.message}
            </span>
          )}
          <input
            id="configstep-input"
            autoFocus
            onChange={this.onChangeName}
            onKeyUp={this.onKeyUpName}
            placeholder="e.g., summer-sale-2025"
            type="text"
            value={this.state.nameInput}
            className="form-control"
            data-test="sequence-name-input"
          />
        </div>

        <div className="form-group">
          <label htmlFor="configstep-targetcampaign">
            Targeted Campaign <span className="required">*</span>
          </label>
          {!campaignValidation.isValid && (
            <span className="text-danger" data-error-code={campaignValidation.code}>
              {campaignValidation.message}
            </span>
          )}
          <select
            id="configstep-targetcampaign"
            onChange={this.onTargetCampaignDropdown}
            value={this.wizardData.targetCampaign?.campaignId || ''}
            className="form-control"
            data-test="sequence-target-campaign-dropdown"
          >
            <option value="" disabled>
              -- Select a Campaign --
            </option>
            {this.wizardData.api_campaignSchedules.map(res => (
              <option key={`campaign_${res.campaignId}`} value={res.campaignId}>
                {res.campaignName} {res.isActive ? '(Active)' : ''}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="configstep-resolution">
            Targeted Screen Resolution <span className="required">*</span>
          </label>
          {!resolutionValidation.isValid && (
            <span className="text-danger" data-error-code={resolutionValidation.code}>
              {resolutionValidation.message}
            </span>
          )}
          <select
            id="configstep-resolution"
            onChange={this.onChangeResolutionDropdown}
            value={resolutionKey}
            className="form-control"
            data-test="sequence-resolution-dropdown"
          >
            {Object.entries(SequenceConfigStep.RESOLUTIONS).map(([key, res]) => (
              <option key={key} value={key}>
                {res.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="configstep-resolution-x">Resolution X</label>
          {!resolutionXValidation.isValid && (
            <span className="text-danger" data-error-code={resolutionXValidation.code}>
              {resolutionXValidation.message}
            </span>
          )}
          <input
            id="configstep-resolution-x"
            type="number"
            min="1"
            className="form-control"
            value={this.state.resolutionX}
            onChange={this.onChangeResolutionX}
            data-test="sequence-resolution-x-input"
          />
        </div>

        <div className="form-group">
          <label htmlFor="configstep-resolution-y">Resolution Y</label>
          {!resolutionYValidation.isValid && (
            <span className="text-danger" data-error-code={resolutionYValidation.code}>
              {resolutionYValidation.message}
            </span>
          )}
          <input
            id="configstep-resolution-y"
            type="number"
            min="1"
            className="form-control"
            value={this.state.resolutionY}
            onChange={this.onChangeResolutionY}
            data-test="sequence-resolution-y-input"
          />
        </div>
      </div>
    );
  }
}
